import { composeReducers } from 'common/util/composeReducers';
import lodash from 'lodash';
import moment from 'moment';
import {
    WRITE_BINDER_VALIDATION,
    LOADING_BINDER_VALIDATION,
    LOADED_BINDER_VALIDATION,
    UNLOAD_BINDER_VALIDATION,
    WRITE_BINDER_VALIDATION_DOCUMENT_UPLOAD,
    WRITE_BINDER_VALIDATION_DOCUMENT_REQUIREMENT,
    WRITE_BINDER_VALIDATION_CHECK_STAFF_MEMBERS,
    WRITE_BINDER_VALIDATION_SYNC_STAFF_MEMBERS,
} from 'pages/binder/actions/binderValidation';


function retrieveBinderValidation(state, action){
    if(!action || action.type !== WRITE_BINDER_VALIDATION){
        return state;
    }

    const values = action.payload.values;
    
    if(!values) {
        return {
            ...state,
            binder:{
                ...state.binder,
                validation:null
            }
        };
    }

    let { documents, validationValues } = values;

    const documentsGroupedByDocumentTypeId = lodash.chain(documents).map(doc => {
        const descriptorsKeyedById = lodash.keyBy(doc.descriptors, 'id');
        const descriptorsKeyedByName = lodash.keyBy(doc.descriptors, 'name');
        return {
            ...doc,
            ...descriptorsKeyedById,
            ...descriptorsKeyedByName
        };
    }).groupBy('documentType.id').value();

    const documentValidationsTypes = {
        study: {
            documentValidations: [],
            totalCount: 0,
            passCount: 0,
        },
        staff: {
            documentValidations: [],
            totalCount: 0,
            passCount: 0,
        }
    };

    const { study, staff } = documentValidationsTypes;

    validationValues.map(vt => {
        const documentTypeValidator = new DocumentTypeValidator(vt);
        const matchingDocuments = getDocumentsMatchingDocumentTypeAndDescriptor(documentsGroupedByDocumentTypeId, documentTypeValidator);
        return performDocumentValidation(documentValidationsTypes[documentTypeValidator.validationType], documentTypeValidator, matchingDocuments);
    });

    const allDocumentValidations = [...study.documentValidations, ...staff.documentValidations];
    
    return {
        ...state,
        binder: {
            ...state.binder,
            validation: {
                ...state.binder.validation,
                documents: documentsGroupedByDocumentTypeId,
                studyDocument: {
                    validations: lodash.sortBy(study.documentValidations, ['displayOrder']),
                    percentComplete: getPercentComplete(study.passCount, study.totalCount),
                    passCount: study.passCount,
                    totalCount: study.totalCount,
                },
                staffDocument: {
                    validations: getStaffDocumentValidations(staff.documentValidations),
                    percentComplete: getPercentComplete(staff.passCount, staff.totalCount),
                    passCount: staff.passCount,
                    totalCount: staff.totalCount,
                },
                allDocumentValidations: lodash.keyBy(allDocumentValidations, 'id'),
                totalPercentComplete: getPercentComplete(study.passCount+staff.passCount, study.totalCount+staff.totalCount)
            }
        }
    };
}

function filterDocumentsByDescriptorValue(documents, descriptorId, descriptorValue) {
    return lodash.filter(documents, doc => {
        const docDescriptor = lodash.get(doc, `${descriptorId}`);
        return docDescriptor.values.includes(descriptorValue);
    });
}

function getDocumentsMatchingDocumentTypeAndDescriptor(documentsGroupedByDocumentTypeId, documentTypeValidator) {
    const { documentTypeId, descriptorId, descriptorName, validationType } = documentTypeValidator;
    const documentsMatchingDocumentType = lodash.get(documentsGroupedByDocumentTypeId, `${documentTypeId}`, []);
    if(validationType === 'study') {
        return documentsMatchingDocumentType;
    }
    return filterDocumentsByDescriptorValue(documentsMatchingDocumentType, descriptorId, descriptorName);
}

function performDocumentValidation(documentValidationsType, documentTypeValidator, documents) {
    if(documentTypeValidator.isRequired && lodash.isNil(documentTypeValidator.removalDate)) {
        const isValid = documentTypeValidator.validate(documents);
        if (isValid) {
            documentValidationsType.passCount += 1;
        }
        documentValidationsType.totalCount += 1;
    }
    return documentValidationsType.documentValidations.push(documentTypeValidator);
}

class DocumentTypeValidator {
    constructor(validationRules) {
        this.id = validationRules.id;
        this.documentIssues = {};
        this.matchingDocuments = [];
        this.documentTypeId = validationRules.documentType.id;
        this.documentTypeName = validationRules.documentType.displayName;
        this.descriptorName = validationRules.descriptorValue;
        this.descriptorId = validationRules.descriptorId;
        this.displayOrder = validationRules.displayOrder;
        this.expectedNumOfCopies = validationRules.expectedNumOfCopies;
        this.isRequired = validationRules.isRequired;
        this.validationType = validationRules.validationType;
        this.removalDate = validationRules.removalDate;
        this.syncDate = validationRules.syncDate;
        this.actualNumOfCopies = 0;
        this.expiringSoonCount = 0;
        this.expiredCount = 0;
        this.validCount = 0;
        this.isValid = false;
    }

    setIsRequired(isRequired) {
        this.isRequired = isRequired;
    }

    resetValidation() {
        this.actualNumOfCopies = 0;
        this.expiringSoonCount = 0;
        this.expiredCount = 0;
        this.validCount = 0;
        this.isValid = false;
    }

    validate(matchingDocuments, overrideSkip) {
        if (this.isRequired || overrideSkip) {
            if(!lodash.isEmpty(matchingDocuments) && lodash.isNil(this.removalDate)) {
                this.validateNumOfCopies(matchingDocuments);
                this.validateDocuments(matchingDocuments);
    
                return this.finalizeValidation();
            }
        }
    }

    validateNumOfCopies(matchingDocuments) {
        this.actualNumOfCopies = matchingDocuments.length;
        if(this.expectedNumOfCopies !== -1) {
    
            if(this.actualNumOfCopies !== this.expectedNumOfCopies) {
                const copyLabel = this.expectedNumOfCopies > 1 ? 'copies' : 'copy';
                const issueLabel = `Expected ${this.expectedNumOfCopies} ${copyLabel} but ${this.actualNumOfCopies} have been filed`;
                return this.addDocumentIssue('tooManyCopies', issueLabel);
            }
        }
    }

    validateDocuments(matchingDocuments) {
        this.matchingDocuments = matchingDocuments.map(doc => {
            const document = new Document(doc);
            document.validate();
            return document;
        });
    }

    finalizeValidation() {
        lodash.map(this.matchingDocuments, doc => {
            if (doc.isExpiringSoon) {
                this.validCount += 1;
                return this.expiringSoonCount += 1;
            }
            if (doc.isExpired) {
                return this.expiredCount += 1;
            }
            if (doc.isValid) {
                return this.validCount += 1;
            }
        });
        this.isValid = this.validCount === this.actualNumOfCopies && lodash.isEmpty(this.documentIssues);
        return this.isValid;
    }

    addDocumentIssue(name, issue) {
        lodash.set(this.documentIssues, `${name}.label`, issue);
    }
}

class Document {
    constructor(document) {
        this.id = document.id;
        this.document = document;
        this.name = document.name;
        this.expirationDate = null;
        this.daysTilExpiration = null;
        this.isExpiringSoon = false;
        this.isExpired = false;
        this.isValid = false;
    }

    validate() {
        this.validateDescriptors();

        if(!this.isExpired) {
            this.isValid = true;
        }
    }

    validateDescriptors() {
        const documentExpirationDate = lodash.get(this.document, 'expiration_date.values[0]', null);
        if(documentExpirationDate) {
            this.validateExpirationDate(documentExpirationDate);
        }
    }

    validateExpirationDate(documentExpirationDate) {
        const expirationDate = moment(documentExpirationDate);
        if(expirationDate.isValid()) {
            const today = moment();
            const eightWeeksAfterToday = moment().add(8, 'weeks');
            
            this.expirationDate = expirationDate;
            this.daysTilExpiration = expirationDate.diff(today, 'days');
            this.isExpiringSoon = expirationDate.isBetween(today, eightWeeksAfterToday);
            this.isExpired = expirationDate.isBefore(today);
        }
    }
}

function getPercentComplete(passCount, totalCount) {
    return Math.floor(passCount/totalCount * 100);
}

function getStaffDocumentValidations(staffDocumentValidations) {

    return lodash.chain(staffDocumentValidations)
        .sortBy('descriptorName')
        .groupBy('descriptorName')
        .value();
}

function setValidationRequirement(state, action) {
    if(!action || action.type !== WRITE_BINDER_VALIDATION_DOCUMENT_REQUIREMENT){
        return state;
    }

    const { validationId, isRequired } = action.payload;
    const newState = lodash.clone(state);

    const changedDocumentValidation = lodash.get(newState, `binder.validation.allDocumentValidations[${validationId}]`);
    const documentsGroupedByDocumentTypeId = lodash.get(newState, 'binder.validation.documents');
    const documentsMatchingDocumentValidation = getDocumentsMatchingDocumentTypeAndDescriptor(documentsGroupedByDocumentTypeId, changedDocumentValidation);

    const validationSection = lodash.get(newState, `binder.validation.${changedDocumentValidation.validationType}Document`);
    const overrideValidationSkip = true;

    changedDocumentValidation.resetValidation();
    changedDocumentValidation.setIsRequired(isRequired);
    changedDocumentValidation.validate(documentsMatchingDocumentValidation, overrideValidationSkip);

    const updatedValidationSection = updateValidationSectionCount(validationSection, changedDocumentValidation.isValid, isRequired);
    
    lodash.set(newState, `binder.validation.allDocumentValidations[${validationId}]`, changedDocumentValidation);
    lodash.set(newState, `binder.validation.${changedDocumentValidation.validationType}Document`, updatedValidationSection);
    return newState;
}

function updateValidationSectionCount(validationSection, isValid, isRequired) {
    const addingDocumentRequirementForValidDocument = isRequired && isValid;
    const removingDocumentRequirementForValidDocument = !isRequired && isValid;
    const addingOrRemovingDocumentRequirementForInvalidDocument = !isValid;

    switch (true) {
        case addingDocumentRequirementForValidDocument:
            validationSection.passCount+=1;
            validationSection.totalCount+=1;
            break;
        case removingDocumentRequirementForValidDocument:
            validationSection.passCount-=1;
            validationSection.totalCount-=1;
            break;
        case addingOrRemovingDocumentRequirementForInvalidDocument:
            validationSection.totalCount = isRequired ? validationSection.totalCount +=1 : validationSection.totalCount -=1;
            break;
        default:
            break;
    }

    validationSection.percentComplete = getPercentComplete(validationSection.passCount, validationSection.totalCount);
    return validationSection;
}

function checkBinderValidationStaffMembers(state, action) {
    if(!action || action.type !== WRITE_BINDER_VALIDATION_CHECK_STAFF_MEMBERS) {
        return state;
    }

    const { syncRequired, staffAdded, staffReAdded, staffRemoved } = action.payload;
    const staffValidations = lodash.get(state, 'binder.validation.staffDocument.validations', {});

    const staffMembersAdded = formatStaffMemberChanges(staffValidations, staffAdded);
    const staffMembersReAdded = formatStaffMemberChanges(staffValidations, staffReAdded);
    const staffMembersRemoved = formatStaffMemberChanges(staffValidations, staffRemoved);

    const newState = lodash.clone(state);
    lodash.set(newState, 'binder.validation.staffMemberChanges', {syncRequired, staffMembersAdded, staffMembersReAdded, staffMembersRemoved});
    return newState;
}

function formatStaffMemberChanges(staffValidations, staff) {
    return lodash.chain(staff).map(staffName => {
        const staffRelatedDocumentValidations = staffValidations[staffName];
        return {
            name: staffName,
            validationIds: lodash.map(staffRelatedDocumentValidations, srdv => srdv.id)
        };
    }).keyBy('name').value();
}

function syncBinderValidationStaffMembers(state, action) {
    if(!action || action.type !== WRITE_BINDER_VALIDATION_SYNC_STAFF_MEMBERS) {
        return state;
    }

    const newState = lodash.clone(state);
    lodash.set(newState, 'binder.validation.staffMemberChanges.syncRequired', false);
    return newState;
}

function loadingBinderValidation(state, action){
    if(!action || (action.type !== LOADING_BINDER_VALIDATION && action.type !== LOADED_BINDER_VALIDATION)){
        return state;
    }

    
    const loading = action.type === LOADING_BINDER_VALIDATION;
    
    const newState = lodash.clone(state);
    lodash.set(newState, 'binder.validation.loading', loading);
    return newState;
}

function unloadBinderValidation(state, action){
    if(!action || action.type !== UNLOAD_BINDER_VALIDATION){
        return state;
    }

    const newState = lodash.clone(state);
    lodash.unset(newState, 'binder.validation');
    return newState;
}

function writeUploadedDocument(state, action) {
    if(!action || action.type !== WRITE_BINDER_VALIDATION_DOCUMENT_UPLOAD) {
        return state;
    }

    const { lastUploadedDocument, documentTypeId } = action.payload;

    const newState = lodash.clone(state);
    lodash.set(newState, `binder.validation.uploads.${documentTypeId}.${lastUploadedDocument.id}`, lastUploadedDocument);

    return newState;
}


export default composeReducers(
    retrieveBinderValidation,
    loadingBinderValidation,
    unloadBinderValidation,
    writeUploadedDocument,
    setValidationRequirement,
    checkBinderValidationStaffMembers,
    syncBinderValidationStaffMembers,
);